<template>
  <div class="row">
    <div class="col-12">
      <p class="text-small text-primary mb-2 font-weight-bold">
        {{ trans("courses.multicircle_exercise.step_1") }}
      </p>
      <h4 class="text-primary mb-4">{{ trans("courses.multicircle_exercise.add_role_title") }}</h4>
      <p class="text-caption mb-2">{{ trans("courses.multicircle_exercise.add_role_limits") }}</p>
      <p class="text-caption font-weight-bold mb-0">
        {{ trans("courses.multicircle_exercise.add_role_limits_2") }}
      </p>
      <p class="text-caption mb-4">{{ trans("courses.multicircle_exercise.add_role_limits_3") }}</p>
      <b-card class="input-background">
        <div class="row">
          <div class="col-9 col-sm-10 d-flex align-items-center">
            <autocomplete-input
              id="role-input"
              v-model="roleInput"
              @input="onInputChange"
              v-debounce:200ms="onDebounce"
              :loading="autocompleteLoading"
              :options="possibleRoles"
              server-filter
              :min-characters="minLength"
              :placeholder="trans('courses.multicircle_exercise.input_placeholder')"
              @select="onSuggestionSelect"
            />
          </div>
          <div class="col-3 col-sm-2 d-flex justify-content-center align-items-center">
            <b-button variant="link" class="shadow-none" :disabled="!canAddRole" @click="saveRole">
              <b-icon icon="plus-circle-fill" variant="info" font-scale="2.5" />
            </b-button>
          </div>
        </div>
      </b-card>

      <div class="row w-100 mx-0" id="roles-container" v-if="roles.length > 0">
        <div class="col-12 mt-2 px-0">
          <b-card class="input-background mb-2" no-body v-for="(role, index) in roles" :key="index">
            <div class="d-flex justify-content-between align-items-center">
              <p class="mb-0 pl-3">{{ role }}</p>
              <b-button variant="warning" class="shadow-none py-0" @click="removeRole(index)">
                <b-icon icon="dash" variant="light" font-scale="2" />
              </b-button>
            </div>
          </b-card>
        </div>
      </div>

      <div v-if="roles.length > 0">
        <p class="text-small font-weight-bold mb-0 mt-4 line-height-normal">
          {{ trans("courses.multicircle_exercise.remove_role_title") }}
        </p>
        <p
          class="text-small mb-0 line-height-normal"
          v-html="trans('courses.multicircle_exercise.remove_role_description')"
        />
      </div>

      <div class="col-12 px-0 mt-3">
        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            :disabled="loading || roles.length < 2"
            @click="saveRoles"
            class="btn-block-xs"
            >{{ trans("courses.multicircle_exercise.next") }}</b-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AutocompleteInput from "../../molecules/form/AutocompleteInput";

export default {
  name: "AddRoles",
  components: { AutocompleteInput },
  data() {
    return {
      roleInput: "",
      roles: [],
      loading: false,
      possibleRoles: [],
      autocompleteLoading: false,
      minLength: 2,
      maxLength: 15,
      maxRolesLength: 8,
      validRoleLength: 3,
    };
  },
  methods: {
    ...mapActions({
      postRoles: "multime/postRoles",
    }),
    saveRole() {
      this.roles.push(this.roleInput);
      this.roleInput = "";
    },
    removeRole(index) {
      if (this.roles.length === 1) {
        this.roles = [];
      } else {
        this.roles.splice(index, 1);
      }
    },
    async saveRoles() {
      this.loading = true;
      try {
        await this.postRoles(this.roles);
        trackEvent("MulticircleExerciseStep1", this.trackingData);
        this.loading = false;
      } catch (err) {
        this.$bvToast.toast(err.response.data.roles[0], {
          variant: "danger",
          solid: true,
        });
        this.loading = false;
      }
    },
    async onDebounce() {
      if (!this.validRole) return;
      const trimmedRole = this.roleInput.trim().replace(/ +/g, " ");
      try {
        const res = await this.$axios.get(`/api/rest/multime/autocomplete?term=${trimmedRole}`);
        if (this.validRole && res.data) {
          this.possibleRoles = res.data.map((role) => {
            return { text: role.role_title, value: role.role_title };
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.autocompleteLoading = false;
      }
    },
    onInputChange() {
      this.possibleRoles = [];
      this.autocompleteLoading = this.validRole;
    },
    onSuggestionSelect(role) {
      this.roleInput = role;
    },
  },
  computed: {
    ...mapGetters({
      trackingData: "multime/getTrackingData",
    }),
    canAddRole() {
      return (
        this.trimmedRole.length >= this.minLength &&
        !this.roles.includes(this.trimmedRole) &&
        this.trimmedRole.length <= this.maxLength &&
        this.roles.length < this.maxRolesLength
      );
    },
    trimmedRole() {
      return this.roleInput.split(" ").join("");
    },
    validRole() {
      return this.trimmedRole.length >= this.validRoleLength;
    },
  },
};
</script>

<style scoped lang="scss">
.input-background {
  background-color: $color-dark-200;
}
</style>
